import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Space,
  Button,
  Table,
  message,
  Tag,
  Modal,
  Spin,
} from "antd";
import { fetchCustomers } from "../customer/FetchCustomers";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import moment from "moment";
import { EditOutlined } from "@ant-design/icons";
import { token } from "../../constants/Constants";
import { create_bmi, fetch_all_bmi, fetch_family_members_by_customer, update_bmi } from "../../endpoints/API";

const BMI = () => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [familymember, setFamilymember] = useState([]);
  const [bmiRecords, setBmiRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [selectedCustomerProfile, setSelectedCustomerProfile] = useState(null);
  const [form] = Form.useForm();
  const user = useSelector((state) => state.user.user);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConclusion, setSelectedConclusion] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showModal = (conclusion) => {
    setSelectedConclusion(conclusion);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const fetchCustomerData = async () => {
    if (selectedCustomerProfile) {
      try {
        const response = await fetch_family_members_by_customer(selectedCustomerProfile);

        if (!response.statusText=="OK") {
          throw new Error("Network response was not ok");
        }

        const data = response?.data
        setFamilymember(data);
      } catch (error) {
        message.error(error?.response?.data?.error||"Error fetching family members")
      }
    }
  };

  const fetchBMIRecords = async () => {
    try {
      setIsLoading(true);
      const response = await fetch_all_bmi();

      if (!response.statusText=="OK") {
        throw new Error("Failed to fetch BMI records");
      }

      const data = response?.data;
      setBmiRecords(data.bmi_records);
    } catch (error) {
      message.error(error?.response?.data?.error||"Failed to load BMI records.")
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, [selectedCustomerProfile]);

  useEffect(() => {
    const getCustomerData = async () => {
      setIsLoading(true);
      try {
        await fetchCustomers(setCustomers, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(error?.response?.data?.error||"Failed to fetch customer data.")
      } finally {
        setIsLoading(false);
      }
    };
    getCustomerData();
    fetchBMIRecords();
  }, []);

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setSelectedCustomerProfile(record.customer.id);
    form.setFieldsValue({
      customer: record.customer.id,
      familymember: record.family_member.id,
      weight: record.weight,
      bodyFatPercentage: record.body_fat_percentage,
      bodyFatKg: record.body_fat_kg,
      subcutaneousFat: record.subcutaneous_fat_percentage,
      skeletalMusclePercentage: record.skeletal_muscle_percentage,
      visceralFat: record.visceral_fat_level,
      restingMetabolism: record.resting_metabolism_kcal,
      bodyAge: record.body_age,
      bmi: record.bmi,
      subcutaneousFatArms: record.subcutaneous_fat_arms,
      subcutaneousFatTrunk: record.subcutaneous_fat_trunk,
      subcutaneousFatLegs: record.subcutaneous_fat_legs,
      skeletalMuscleKg: record.skeletal_muscle_kg,
      skeletalMuscleArms: record.skeletal_muscle_arms,
      skeletalMuscleTrunk: record.skeletal_muscle_trunk,
      skeletalMuscleLegs: record.skeletal_muscle_legs,
      systolic: record.systolic,
      diastolic: record.diastolic,
    });
    setIsFormVisible(true);
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    const createdBy = user.id;
    try {
      const response = selectedRecord
        ? await update_bmi(selectedRecord.id, { ...values, createdBy })
        : await create_bmi({ ...values, createdBy });

      if (response.statusText=="OK") {
        message.success(
          `BMI ${selectedRecord ? "updated" : "created"} successfully`
        );
        setIsFormVisible(false);
        setSelectedRecord(null);
        fetchBMIRecords();
        form.resetFields();
      } else {
        message.error("Submission failed.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error||"An error occurred while submitting.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      fixed: "left",
      width: 140,
      render: (customer) =>
        customer
          ? `${customer.first_name} ${customer.middle_name || ""} ${customer.last_name}`
          : "N/A",
    },
    {
      title: "Family Member",
      dataIndex: "family_member",
      key: "family_member",
      width: 160,
      render: (family_member) =>
        family_member ? (
          <>
            {`${family_member.first_name} ${family_member.middle_name || ""} ${family_member.last_name}`}
            <Tag>{family_member?.family_member_type?.name}</Tag>
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Date-Time",
      dataIndex: "date_time",
      key: "date_time",
      width: 150,
      render: (text) => (
        <>
          <Tag color="blue">{moment(text).format("DD-MM-YYYY")}</Tag>
          <Tag color="green">{moment(text).format("hh:mm A")}</Tag>
        </>
      ),
    },
    {
      title: "Weight",
      dataIndex: "weight",
      key: "weight",
      width: 90,
      render: (weight) => (weight ? `${weight} Kg` : "N/A"),
    },
    {
      title: "Body Fat (%)",
      dataIndex: "body_fat_percentage",
      key: "body_fat_percentage",
      width: 100,
      render: (value) => (value ? `${value} %` : "N/A"),
    },
    {
      title: "Subcutaneous Fat (%)",
      dataIndex: "subcutaneous_fat_percentage",
      key: "subcutaneous_fat_percentage",
      width: 130,
      render: (value) => (value ? `${value} %` : "N/A"),
    },
    {
      title: "Skeletal Muscle (Kg)",
      dataIndex: "skeletal_muscle_kg",
      key: "skeletal_muscle_kg",
      width: 130,
      render: (value) => (value ? `${value} Kg` : "N/A"),
    },
    {
      title: "Visceral Fat",
      dataIndex: "visceral_fat_level",
      key: "visceral_fat_level",
      width: 100,
    },
    {
      title: "Resting Metabolism",
      dataIndex: "resting_metabolism_kcal",
      key: "resting_metabolism_kcal",
      width: 140,
      render: (value) => (value ? `${value} kcal` : "N/A"),
    },
    {
      title: "Body Age",
      dataIndex: "body_age",
      key: "body_age",
      width: 90,
    },
    {
      title: "BMI",
      dataIndex: "bmi",
      key: "bmi",
      width: 70,
    },
    {
      title: "Systolic",
      dataIndex: "systolic",
      key: "systolic",
      width: 90,
    },
    {
      title: "Diastolic",
      dataIndex: "diastolic",
      key: "diastolic",
      width: 90,
    },
    {
      title: "Smart Report",
      key: "conclusion",
      width: 110,
      render: (_, record) => (
        <Button size="small" onClick={() => showModal(record.conclusion)}>View</Button>
      ),
    },
    {
      title: "Edit",
      key: "edit",
      width: 60,
      fixed: "right",
      render: (_, record) => (
        <EditOutlined
          style={{ cursor: "pointer", color: "blue" }}
          onClick={() => handleEdit(record)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "8px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <Button type="primary" onClick={() => setIsFormVisible(true)}>
          Create BMI
        </Button>
      </div>

      {isFormVisible ? (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>

            {/* Customer & Family Member */}
            <div className="bg-gray-100 px-5 py-2 rounded-lg">
              <Form.Item
                label="Select Customer"
                name="customer"
                rules={[
                  { required: true, message: "Please select a customer" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select a customer"
                  onChange={(value) => setSelectedCustomerProfile(value)}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  options={customers?.map((customer) => ({
                    value: customer.id,
                    label: `${customer.first_name} ${customer.middle_name ? customer.middle_name + " " : ""}${customer.last_name} (${customer.email}) (${customer.phone})`,
                  }))}
                  disabled={!!selectedRecord}
                />
              </Form.Item>

              <Form.Item
                label="Family Member"
                name="familymember"
                rules={[
                  { required: true, message: "Please select a family member" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select family member"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  options={familymember?.map((item) => ({
                    value: item.id,
                    label: `${item.first_name} ${item.middle_name ? item.middle_name + " " : ""}${item.last_name ? item.last_name : ""}`,
                  }))}
                />
              </Form.Item>
            </div>

            {/* Weight & Body Fat */}
            <div className="bmi-row-group bg-gray-100 px-5 py-2 rounded-lg">
              <Form.Item label="Weight" name="weight">
                <Input placeholder="Enter Weight" />
              </Form.Item>
              <Form.Item label="Body Fat (%)" name="bodyFatPercentage">
                <Input placeholder="Enter Body Fat (%)" />
              </Form.Item>
            </div>

            {/* Subcutaneous Fat & Skeletal Muscle */}
            <div className="bmi-row-group bg-gray-100 px-5 py-2 rounded-lg">
              <Form.Item label="Subcutaneous Fat" name="subcutaneousFat">
                <Input placeholder="Enter Subcutaneous Fat" />
              </Form.Item>
              <Form.Item label="Skeletal Muscle (kg)" name="skeletalMuscleKg">
                <Input placeholder="Enter Skeletal Muscle (kg)" />
              </Form.Item>
            </div>

            {/* Visceral Fat & Resting Metabolism */}
            <div className="bmi-row-group bg-gray-100 px-5 py-2 rounded-lg">
              <Form.Item label="Visceral Fat" name="visceralFat">
                <Input placeholder="Enter Visceral Fat" />
              </Form.Item>
              <Form.Item label="Resting Metabolism" name="restingMetabolism">
                <Input placeholder="Enter Resting Metabolism" />
              </Form.Item>
            </div>

            {/* Body Age & BMI */}
            <div className="bmi-row-group bg-gray-100 px-5 py-2 rounded-lg">
              <Form.Item label="Body Age" name="bodyAge">
                <Input placeholder="Enter Body Age" />
              </Form.Item>
              <Form.Item label="BMI" name="bmi">
                <Input placeholder="Enter BMI" />
              </Form.Item>
            </div>

            <div className="font-semibold text-lg">Blood Pressure</div>

            {/* Systolic & Diastolic */}
            <div className="bmi-row-group bg-gray-100 px-5 py-2 rounded-lg">
              <Form.Item label="Systolic" name="systolic">
                <Input placeholder="Enter Systolic" />
              </Form.Item>
              <Form.Item label="Diastolic" name="diastolic">
                <Input placeholder="Enter Diastolic" />
              </Form.Item>
            </div>

            <div className="flex gap-5 justify-center" style={{ flexWrap: "wrap" }}>
              <Button type="primary" htmlType="submit" disabled={isSubmitting}>
                {isSubmitting ? <Spin size="small" /> : "Submit"}
              </Button>
              <Button onClick={() => setIsFormVisible(false)}>Cancel</Button>
            </div>

          </Space>
        </Form>
      ) : (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <Table
            columns={columns}
            dataSource={[...bmiRecords].sort(
              (a, b) => new Date(b.date_time) - new Date(a.date_time)
            )}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: "max-content" }}
            size="small"
            pagination={{
              responsive: true,
              showSizeChanger: false,
              size: "small",
            }}
          />
        </div>
      )}

      <Modal
        title="Health Report"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width="min(800px, 95vw)"
        style={{ maxWidth: "95vw" }}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <p>{selectedConclusion}</p>
      </Modal>

      <style>{`
        .bmi-row-group {
          display: flex;
          gap: 40px;
          flex-wrap: wrap;
        }
        .bmi-row-group .ant-form-item {
          flex: 1 1 200px;
          min-width: 0;
        }

        @media (max-width: 768px) {
          .bmi-row-group {
            gap: 0;
            flex-direction: column;
          }
          .bmi-row-group .ant-form-item {
            flex: 1 1 100%;
          }
          .ant-table {
            font-size: 12px !important;
          }
          .ant-table-cell {
            padding: 6px 8px !important;
          }
          .ant-btn-sm {
            font-size: 12px !important;
          }
          .ant-tag {
            font-size: 11px !important;
            padding: 0 4px !important;
          }
          .ant-form-item {
            margin-bottom: 10px !important;
          }
          .ant-space {
            width: 100% !important;
          }
          .bg-gray-100 {
            padding: 12px !important;
          }
        }

        @media (max-width: 480px) {
          .ant-modal-content {
            margin: 0 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default BMI;